// Seasons (docs/23). The World keeps a calendar: four seasons of twelve weeks, and every encounter is
// stamped with the season it happens in (`board.season`). The client turns a season into three things,
// all pure functions here:
//
//   1. Day length (`seasonHour`): winter days are short and summer days long, so the clock hour is
//      remapped onto the docs/19 sky keyframes before they are read. 12:00 is noon in every season;
//      17:00 in winter is dusk, 20:00 in summer is still golden.
//   2. A grade (`seasoned`): a fresher green spring, a warm summer, an amber autumn with tired greens,
//      a cold blue winter. Outdoors only; the ambient still goes through `legible()`.
//   3. Dressing: blossom petals in spring, pollen and thistledown by day and more fireflies by night
//      in summer, falling leaves in every wood in autumn, ice glints in the air and frost on the ground
//      in winter. The weather wins where they clash (weather.ts `weathered` runs after this).
//
// No season (an unstamped room, no `?season=`) changes nothing at all.
import { legible, phaseOf, type Climate, type DayLight, type RGB } from '../lighting/daylight';

export type Season = 'spring' | 'summer' | 'autumn' | 'winter';
export const SEASONS: readonly Season[] = ['spring', 'summer', 'autumn', 'winter'];

export function parseSeason(s: string | null | undefined): Season | undefined {
  const k = s?.trim().toLowerCase();
  return k && (SEASONS as readonly string[]).includes(k) ? (k as Season) : undefined;
}

export function seasonLabel(s: Season): string {
  return s === 'spring' ? 'Spring' : s === 'summer' ? 'Summer' : s === 'autumn' ? 'Autumn' : 'Winter';
}

/**
 * Day length as anchors `[clock hour, keyframe hour]`: first light, sunrise, noon, sunset, full dark.
 * The docs/19 keyframes put first light at 4.5, sunrise at 6, sunset at 18 and full dark at 20.5;
 * spring and autumn are the equinox (identity), summer runs 4.5–21, winter 8–16.
 */
export const DAY_ANCHORS: Record<Season, ReadonlyArray<readonly [number, number]>> = {
  spring: [[4.5, 4.5], [6, 6], [12, 12], [18, 18], [20.5, 20.5]],
  summer: [[3.25, 4.5], [4.5, 6], [12, 12], [21, 18], [22.75, 20.5]],
  autumn: [[4.5, 4.5], [6, 6], [12, 12], [18, 18], [20.5, 20.5]],
  winter: [[7, 4.5], [8, 6], [12, 12], [16, 18], [17.5, 20.5]],
};

/** Sunrise and sunset (clock hours) in a season. */
export function daySpan(season: Season): readonly [number, number] {
  const a = DAY_ANCHORS[season];
  return [a[1]![0], a[3]![0]];
}

/** The keyframe hour that a clock hour looks like in a season (pure, monotonic, 12 → 12). */
export function seasonHour(hour: number, season: Season | undefined): number {
  const h = ((hour % 24) + 24) % 24;
  if (!season) return h;
  const a = DAY_ANCHORS[season];
  // walk the anchors round the clock: the last one wraps to the first plus a day
  const pts = [...a, [a[0]![0] + 24, a[0]![1] + 24] as const];
  const x = h < a[0]![0] ? h + 24 : h;
  for (let i = 0; i < pts.length - 1; i++) {
    const [x0, y0] = pts[i]!, [x1, y1] = pts[i + 1]!;
    if (x >= x0 && x <= x1) return (((y0 + ((x - x0) / (x1 - x0)) * (y1 - y0)) % 24) + 24) % 24;
  }
  return h;
}

interface SeasonGrade {
  /** Multiplies the outdoor ambient (floors applied after). */
  ambient: RGB;
  saturation: number; warmth: number; contrast: number;
  /** Hue shift as a 4×5 colour matrix (row-major); white stays (nearly) white. */
  matrix?: readonly number[];
}

/** The seasons' grades, outdoors (docs/23). Small: a season is a mood, the hour and the weather are the picture. */
export const SEASON_GRADE: Record<Season, SeasonGrade> = {
  // fresher: greens a touch greener, a little more colour
  spring: { ambient: [0.99, 1.0, 0.98], saturation: 0.07, warmth: -0.02, contrast: 0, matrix: [1, 0, 0, 0, 0, 0.0, 1.05, -0.03, 0, 0, 0, 0, 0.99, 0, 0, 0, 0, 0, 1, 0] },
  // warm and bright
  summer: { ambient: [1.0, 0.99, 0.95], saturation: 0.05, warmth: 0.12, contrast: 0.03 },
  // amber: greens slide toward olive and gold, a little less colour overall
  autumn: { ambient: [1.0, 0.96, 0.9], saturation: -0.06, warmth: 0.18, contrast: 0, matrix: [0.9, 0.16, 0, 0, 0, 0.04, 0.9, 0.02, 0, 0, 0, 0.02, 0.94, 0, 0, 0, 0, 0, 1, 0] },
  // cold: greens greyed toward the blue, colour drained
  winter: { ambient: [0.95, 0.97, 1.0], saturation: -0.2, warmth: -0.3, contrast: 0.04, matrix: [0.98, 0.02, 0, 0, 0, 0.05, 0.88, 0.07, 0, 0, 0, 0.02, 1, 0, 0, 0, 0, 0, 1, 0] },
};

/** The seasons under the rock (docs/23 Y9): the seep cool and wet, the bloom's glow at its brightest, the spore-fall hazed, the deep still dim and blue. */
export const DEEP_GRADE: Record<Season, SeasonGrade> = {
  spring: { ambient: [0.97, 1.0, 1.02], saturation: 0, warmth: -0.04, contrast: 0 },
  summer: { ambient: [1.0, 1.06, 1.04], saturation: 0.08, warmth: 0, contrast: 0.03 },
  autumn: { ambient: [1.0, 0.98, 0.94], saturation: -0.08, warmth: 0.06, contrast: -0.02 },
  winter: { ambient: [0.94, 0.95, 1.0], saturation: -0.14, warmth: -0.12, contrast: 0.03 },
};

/** The Ashlands' seasons (docs/23 Y10): the waking smoky and steaming, the burning hot and orange, the ashfall grey and dark, the ash winter cold, grey-blue and drained. */
export const ASH_GRADE: Record<Season, SeasonGrade> = {
  spring: { ambient: [0.97, 0.96, 0.95], saturation: -0.06, warmth: 0.02, contrast: -0.02 },
  summer: { ambient: [1.04, 0.97, 0.88], saturation: 0.06, warmth: 0.18, contrast: 0.04 },
  autumn: { ambient: [0.9, 0.89, 0.88], saturation: -0.2, warmth: -0.04, contrast: -0.03 },
  winter: { ambient: [0.9, 0.92, 0.98], saturation: -0.24, warmth: -0.28, contrast: 0.03 },
};

/** Multiply two 4×5 colour matrices (b applied after a). */
export function chainMatrix(a: readonly number[] | undefined, b: readonly number[] | undefined): readonly number[] | undefined {
  if (!a) return b;
  if (!b) return a;
  const out: number[] = new Array<number>(20).fill(0);
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 4; c++) { let v = 0; for (let k = 0; k < 4; k++) v += b[r * 5 + k]! * a[k * 5 + c]!; out[r * 5 + c] = v; }
    let o = b[r * 5 + 4]!; for (let k = 0; k < 4; k++) o += b[r * 5 + k]! * a[k * 5 + 4]!; out[r * 5 + 4] = o;
  }
  return out;
}

/**
 * How much rime the ground shader lays even under a clear sky (0..1 of FROST_CAP): cold and snowy
 * country in winter, and a temperate winter's dawn and night.
 */
export function seasonChill(season: Season | undefined, day: Pick<DayLight, 'feel' | 'phase' | 'setting'>): number {
  if (season !== 'winter' || day.setting !== 'outdoor') return 0;
  if (day.feel === 'snowy' || day.feel === 'cold') return 0.45;
  if (day.feel === 'arid') return day.phase === 'night' || day.phase === 'dawn' ? 0.3 : 0;   // desert winter nights freeze
  if (day.feel === 'tropic') return 0;
  if (day.feel === 'deep') return 0.2;   // the deep still: rime creeping down from the shafts
  if (day.feel === 'ash') return day.phase === 'night' || day.phase === 'dawn' ? 0.2 : 0;   // the ash winter: hoarfrost on the black glass
  return day.phase === 'night' || day.phase === 'dawn' ? 0.25 : 0;
}

/**
 * The hour's light in a season (outdoors: grade and dressing; indoors: nothing — the hour was
 * already remapped). Pure; the ambient goes back through `legible()`.
 */
export function seasoned(day: DayLight, season: Season | undefined, climate: Climate): DayLight {
  if (!season || day.setting !== 'outdoor') return day;
  const feel = day.feel ?? 'temperate';
  const g = feel === 'deep' ? DEEP_GRADE[season] : feel === 'ash' ? ASH_GRADE[season] : SEASON_GRADE[season];
  const night = day.phase === 'night' || day.phase === 'dusk';
  const daylit = !night;
  const atm = { ...day.atmosphere };
  const green = feel !== 'arid' && feel !== 'snowy' && feel !== 'deep' && feel !== 'ash';
  if (feel === 'deep') {
    // the seep drips and mists; the bloom's spores drift and its glow-worms swarm; the spore-fall
    // hazes every hour; the deep still glints with rime and the glow-worms go quiet
    if (season === 'spring') { atm.drips = true; atm.mist = true; }
    if (season === 'summer') { atm.pollen = daylit; atm.motes = true; atm.fireflies = night || day.phase === 'dawn'; }
    if (season === 'autumn') { atm.mist = true; atm.motes = true; }
    if (season === 'winter') { atm.fireflies = false; atm.glints = true; }
  } else if (feel === 'ash') {
    // embers always (the climate); the waking steams, the burning shimmers, the ashfall drifts with
    // ash all day, the ash winter is cold and still with rime glinting at night
    if (season === 'spring') { atm.mist = true; }
    if (season === 'summer') { atm.rising = daylit; }
    if (season === 'autumn') { atm.motes = true; atm.mist = true; }
    if (season === 'winter') { atm.glints = night || day.phase === 'dawn'; }
  } else switch (season) {
    case 'spring':
      atm.petals = daylit && green && feel !== 'tropic';
      break;
    case 'summer':
      atm.pollen = (day.phase === 'day' || day.phase === 'golden') && green;
      atm.fireflies = (!!climate.atmosphere.fireflies || green) && (night || day.phase === 'golden');
      break;
    case 'autumn':
      atm.leaves = daylit && green && feel !== 'tropic' ? true : atm.leaves;
      break;
    case 'winter':
      atm.leaves = false; atm.fireflies = false; atm.petals = false; atm.pollen = false;
      atm.glints = feel !== 'tropic';
      break;
  }
  return {
    ...day,
    ambient: legible([day.ambient[0] * g.ambient[0], day.ambient[1] * g.ambient[1], day.ambient[2] * g.ambient[2]]),
    saturation: day.saturation + g.saturation,
    warmth: day.warmth + g.warmth,
    contrast: (day.contrast ?? 0) + g.contrast,
    matrix: chainMatrix(day.matrix, g.matrix),
    atmosphere: atm,
    chill: Math.max(day.chill ?? 0, seasonChill(season, day)),
    breath: day.breath || season === 'winter' && feel !== 'tropic' && feel !== 'deep' && (feel !== 'arid' && feel !== 'ash' || night),
  };
}

/** Is this clock hour daylight in a season? (HUD and tests.) */
export function isDaylight(hour: number, season: Season | undefined): boolean {
  const p = phaseOf(seasonHour(hour, season));
  return p === 'day' || p === 'golden';
}
